import { useEffect, useRef } from 'react';

interface UseMenuWebSocketOptions {
  onMenuUpdate?: (data: unknown) => void;
}

function getWsUrl(): string {
  try {
    const envUrl = import.meta.env.VITE_WS_URL;
    if (envUrl) return envUrl;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  } catch {
    return '';
  }
}

const HEARTBEAT_INTERVAL = 25000;

export function useMenuWebSocket({ onMenuUpdate }: UseMenuWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval>>();
  const onMenuUpdateRef = useRef(onMenuUpdate);
  onMenuUpdateRef.current = onMenuUpdate;

  useEffect(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl) return;

    let stopped = false;

    function connect() {
      if (stopped) return;
      try {
        const ws = new WebSocket(`${wsUrl}/ws/menu`);
        wsRef.current = ws;

        ws.onopen = () => {
          heartbeatRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, HEARTBEAT_INTERVAL);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'pong') return;
            onMenuUpdateRef.current?.(data);
          } catch {
            // Ignore non-JSON messages
          }
        };

        ws.onclose = () => {
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
          if (!stopped) setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        if (!stopped) setTimeout(connect, 5000);
      }
    }

    connect();

    return () => {
      stopped = true;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      wsRef.current?.close();
    };
  }, []);
}
